import createClient, { type Client } from "openapi-fetch";
import type { paths } from "@/generated/openapi-types";

/**
 * How one request authenticates. Resolved per fetch rather than captured
 * once, so a token Amplify refreshed mid-session goes out on the very next
 * request instead of after a reload.
 */
export type AuthStrategy =
  | { kind: "bearer"; token: string }
  | { kind: "dev-headers"; sub: string; email: string }
  | null;

export type ApiClient = Client<paths>;

export function createApiClient(options: {
  baseUrl: string;
  getAuth: () => AuthStrategy | Promise<AuthStrategy>;
}): ApiClient {
  const { baseUrl, getAuth } = options;
  return createClient<paths>({
    baseUrl,
    fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      const auth = await getAuth();
      if (auth?.kind === "bearer") {
        headers.set("Authorization", `Bearer ${auth.token}`);
      } else if (auth?.kind === "dev-headers") {
        // Only ever reachable from a dev build talking to a dev server —
        // see cognito-config.ts. The deployed Lambda ignores these headers.
        headers.set("x-user-sub", auth.sub);
        headers.set("x-user-email", auth.email);
      }
      return fetch(input, { ...init, headers });
    },
  });
}
